/**
 * Was der globale „Neu"-Dialog anlegen kann — `07` §7.3.
 *
 * Der Satz dort lautet: „Ein globaler ‚Neu'-Dialog erstellt kontextbezogen
 * Auftrag, Aufgabe, Termin, Nachricht oder Dokumentverknüpfung."
 *
 * **Einer von fünfen geht, und das steht dabei.** Ein Auftrag ist eine
 * Portal-Ressource; die anderen vier leben in Vikunja, Nextcloud und Stalwart
 * und brauchen ihren Adapter. Alle fünf abzuschalten wäre ehrlicher gewesen,
 * solange keiner ging — jetzt wäre es das Gegenteil.
 *
 * **Kontextbezogen ist er noch nicht.** Er soll den offenen Auftrag
 * übernehmen; dafür müsste er wissen, welcher gerade offen ist. Der Weg dahin
 * ist kurz — die Auftragsakte kennt ihn —, aber er gehört zu dem Paket, das
 * den Kontext führt, und nicht in einen Dialog, der ihn errät.
 */

import { useState } from "react";
import { useNavigate } from "react-router";

import { darfEingangBearbeiten } from "@hwc/auth";
import { Knopf, Zustand } from "@hwc/ui";

import { useSitzung } from "./Sitzung.js";
import { legeAuftragAn } from "./vorgaenge.js";

export function NeuerAuftragImDialog() {
  const { bestand, person, rollen, fuehreAus } = useSitzung();
  const navigiere = useNavigate();

  const [offen, setzeOffen] = useState(false);
  const [titel, setzeTitel] = useState("");
  const [kunde, setzeKunde] = useState("");
  const [gewerk, setzeGewerk] = useState("");
  const [absage, setzeAbsage] = useState<string | null>(null);

  // Wer den Eingangskorb nicht bearbeiten darf, legt auch keine Aufträge an:
  // `07` §7.2 führt „Auftrag anlegen und planen" unter Büro/Disposition.
  if (!darfEingangBearbeiten(rollen)) return null;

  if (!offen) {
    return (
      <div className="filter">
        <Knopf type="button" art="haupt" onClick={() => setzeOffen(true)}>
          Auftrag anlegen
        </Knopf>
      </div>
    );
  }

  return (
    <div className="formularblock">
      {absage && <Zustand art="warnung" text={absage} />}

      <label className="feld">
        <span className="feld__name">Titel</span>
        <input
          className="suchfeld"
          type="text"
          value={titel}
          onChange={(e) => setzeTitel(e.target.value)}
          placeholder="Was ist zu tun?"
          autoFocus
        />
      </label>

      <label className="feld">
        <span className="feld__name">Kunde</span>
        <select
          className="suchfeld"
          value={kunde}
          onChange={(e) => setzeKunde(e.target.value)}
        >
          <option value="">— bitte wählen —</option>
          {bestand.kunden.map((k) => (
            <option key={k.ref.opaqueId} value={k.ref.opaqueId}>
              {k.nummer} — {k.name}
            </option>
          ))}
        </select>
      </label>

      <label className="feld">
        <span className="feld__name">Gewerk (freiwillig)</span>
        <input
          className="suchfeld"
          type="text"
          value={gewerk}
          onChange={(e) => setzeGewerk(e.target.value)}
          placeholder="z. B. Installation"
        />
      </label>

      <div className="filter">
        <Knopf
          type="button"
          art="haupt"
          disabled={!titel.trim() || !kunde}
          onClick={() => {
            const treffer = bestand.kunden.find((k) => k.ref.opaqueId === kunde);
            if (!treffer) return;

            let nummer: string | undefined;
            const grund = fuehreAus((v) => {
              const ergebnis = legeAuftragAn({
                vorgaenge: v,
                bestand,
                titel,
                kunde: treffer.ref,
                verantwortlich: person.ref,
                gewerk: gewerk.trim() || undefined,
              });
              if (ergebnis.ok) nummer = ergebnis.vorgaenge.neueAuftraege.at(-1)?.nummer;
              return ergebnis;
            });

            if (grund) {
              setzeAbsage(grund);
              return;
            }
            // **Direkt in die neue Akte.** Wer etwas anlegt, will es sehen —
            // und ein Dialog, der sich schließt und den Benutzer dort
            // zurücklässt, wo er war, lässt ihn suchen.
            setzeTitel("");
            setzeKunde("");
            setzeGewerk("");
            setzeOffen(false);
            if (nummer) navigiere(`/auftraege/${nummer}`);
          }}
        >
          Anlegen und öffnen
        </Knopf>
        <Knopf
          type="button"
          art="neben"
          onClick={() => {
            setzeOffen(false);
            setzeAbsage(null);
          }}
        >
          Abbrechen
        </Knopf>
      </div>
    </div>
  );
}
